import { z } from 'zod'
import {
  TRAINER_CONTRACT_VERSION,
  trainerReadSideEffectsSchema,
} from './trainer-event-read'
export const trainerResourceReadSideEffectsSchema = trainerReadSideEffectsSchema.extend({
  r2Touched: z.literal(false),
})


const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerResourceListQuerySchema = z.object({
  orgId: optionalQueryString,
  search: optionalQueryString,
  type: optionalQueryString,
  course_type: optionalQueryString,
  courseType: optionalQueryString,
  language: optionalQueryString,
  includeArchived: optionalQueryString,
  include_archived: optionalQueryString,
})

export const trainerResourceDetailQuerySchema = z.object({
  orgId: optionalQueryString,
  includeArchived: optionalQueryString,
  include_archived: optionalQueryString,
})

export const trainerResourceRetentionPolicyQuerySchema = z.object({
  orgId: optionalQueryString,
})

export const trainerResourceFileLinkHealthSchema = z.object({
  status: z.enum(['ready', 'missing_url', 'invalid_url', 'unsupported_protocol']),
  openable: z.boolean(),
  reason: z.string(),
  urlReturned: z.boolean(),
  httpProbePerformed: z.literal(false),
  signedPrivateDownloadAvailable: z.boolean(),
  uploadMutationAvailable: z.boolean(),
})

export const trainerResourceFileSchema = z.object({
  id: z.string().min(1),
  resourceId: z.string().nullable(),
  name: z.string(),
  url: z.string().nullable(),
  uploadId: z.string().nullable(),
  linkHealth: trainerResourceFileLinkHealthSchema,
  version: z.string().nullable(),
  language: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  courseType: z.string().nullable(),
  courseTypeId: z.string().nullable(),
  category: z.string().nullable(),
  availableForTen: z.boolean().nullable(),
  sourceUpdatedDate: z.string().nullable(),
  downloadCount: z.number().int().nonnegative(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const trainerResourceSourceStateSchema = z.object({
  kind: z.enum(['external_link', 'upload_metadata', 'metadata_only']),
  status: z.enum(['ready', 'provider_gated', 'incomplete']),
  fileSourceMaterialized: z.boolean(),
  storageActionAvailable: z.boolean(),
  providerCredentialsReturned: z.literal(false),
})

export const trainerResourceSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  name: z.string(),
  desc: z.string().nullable(),
  sourceLink: z.string().nullable(),
  type: z.string().nullable(),
  imageUrl: z.string().nullable(),
  noOfFiles: z.number().int().nonnegative(),
  upload: z.object({
    uploadId: z.string().nullable(),
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSizeBytes: z.number().int().nonnegative().nullable(),
    r2Touched: z.literal(false),
  }),
  sourceState: trainerResourceSourceStateSchema,
  files: z.array(trainerResourceFileSchema).optional(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const trainerResourceReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.union([
    z.tuple([z.literal('trainer_resources')]),
    z.tuple([z.literal('trainer_resources'), z.literal('trainer_resource_files')]),
    z.tuple([
      z.literal('trainer_resources'),
      z.literal('trainer_resource_files'),
      z.literal('trainer_resource_downloads'),
    ]),
  ]),
  projection: z.enum([
    'trainer-resource-metadata',
    'trainer-resource-metadata-with-file-filters',
    'trainer-resource-detail-with-files',
    'trainer-resource-download-history',
    'trainer-resource-link-audit',
  ]),
  rawPayloadReturned: z.literal(false),
  archivedIncluded: z.boolean().optional(),
})

export const trainerResourceListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  resources: z.array(trainerResourceSummarySchema),
  resourceEvidence: trainerResourceReadEvidenceSchema,
  sideEffects: trainerResourceReadSideEffectsSchema,
})

export const trainerResourceDetailResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  resource: trainerResourceSummarySchema,
  resourceEvidence: trainerResourceReadEvidenceSchema,
  sideEffects: trainerResourceReadSideEffectsSchema,
})

export const trainerResourceRetentionPolicyResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  policy: z.object({
    scope: z.literal('trainer_resources'),
    db1RetentionMode: z.literal('soft_archive_only'),
    hardDeleteAvailable: z.literal(false),
    hardDeleteRouteAvailable: z.literal(false),
    migratedMetadataRetained: z.literal(true),
    providerCleanupAvailable: z.literal('archived_uploads_backed_files_only'),
    providerCleanupDb1RowRetained: z.literal(true),
    restoreDb1MetadataAvailable: z.literal(true),
    backupRequiredBeforeFutureHardDelete: z.literal(true),
    newLedgerRequiredBeforeFutureHardDelete: z.literal(true),
    retentionDecision: z.literal('retain_db1_records_indefinitely_until_approved_policy'),
  }),
  safeguards: z.object({
    idempotencyRequired: z.literal(true),
    auditRequired: z.literal(true),
    providerHeadersReturned: z.literal(false),
    objectKeyReturned: z.literal(false),
    rawPayloadReturned: z.literal(false),
    destructiveDbCleanup: z.literal(false),
  }),
  sideEffects: trainerResourceReadSideEffectsSchema,
})
export type TrainerResourceReadSideEffects = z.infer<typeof trainerResourceReadSideEffectsSchema>

export type TrainerResourceListQuery = z.infer<typeof trainerResourceListQuerySchema>
export type TrainerResourceDetailQuery = z.infer<typeof trainerResourceDetailQuerySchema>
export type TrainerResourceRetentionPolicyQuery = z.infer<typeof trainerResourceRetentionPolicyQuerySchema>
export type TrainerResourceFileLinkHealth = z.infer<typeof trainerResourceFileLinkHealthSchema>
export type TrainerResourceFile = z.infer<typeof trainerResourceFileSchema>
export type TrainerResourceSourceState = z.infer<typeof trainerResourceSourceStateSchema>
export type TrainerResourceSummary = z.infer<typeof trainerResourceSummarySchema>
export type TrainerResourceReadEvidence = z.infer<typeof trainerResourceReadEvidenceSchema>
export type TrainerResourceListResponse = z.infer<typeof trainerResourceListResponseSchema>
export type TrainerResourceDetailResponse = z.infer<typeof trainerResourceDetailResponseSchema>
export type TrainerResourceRetentionPolicyResponse = z.infer<typeof trainerResourceRetentionPolicyResponseSchema>
